import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'sonner';

import { APIBaseChronos } from '../hosts';
import { QueryEntity } from '../types';
import useGetFetchConfig, { FetchMethod } from '../useGetFetchConfig';
import { Classification, Relevance } from '@/types';

interface ClusterFactsRequest {
  factIds: string[];
  description: string;
  author?: string;
  factDate?: string;
  factDateText?: string;
  relevance?: Relevance;
  significance?: string;
  topic?: string;
  issues?: string[];
  classification?: Classification;
  isSupplementaryFact?: boolean;
  sourceText?: string;
}

interface UseClusterFactsOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const useClusterFacts = (caseId: string, options?: UseClusterFactsOptions) => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  return useMutation<any, Error, ClusterFactsRequest>({
    mutationFn: async (data) => {
      const fetchConfig = getFetchConfig({
        method: FetchMethod.POST,
        data,
      });

      const response = await fetch(`${APIBaseChronos}/api/case/${caseId}/fact/cluster`, fetchConfig);

      if (!response.ok) {
        throw new Error('Failed to cluster facts');
      }

      return response.json();
    },
    onSuccess: async () => {
      toast.success('Facts clustered successfully');

      queryClient.setQueryData([QueryEntity.IncludedFactsCount, { caseId }], 0);

      await queryClient.invalidateQueries({ queryKey: [QueryEntity.CaseFacts] });
      await queryClient.invalidateQueries({ queryKey: [QueryEntity.IncludedFactsCount, { caseId }] });

      options?.onSuccess?.();
    },
    onError: (error) => {
      console.error('Error clustering facts:', error);
      toast.error('Failed to cluster facts');
      options?.onError?.(error);
    },
  });
};
